import type { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { EllipsisVerticalIcon } from 'lucide-react';
import PlusIcon from '@/shared/assets/plus-icon.svg?react';
import TransferIcon from '@/shared/assets/arrows-down-up-icon.svg?react';
import { NUMERIC_CONFIG } from '@/shared/config';
import { formatBalance } from '@/shared/lib';
import { cn } from '@/shared/utils';
import type { AccountType } from '@/entities/account';
import { useAccountCreateModal } from '@/widgets/account-create';

type Props = {
  type: AccountType;
  accountTitle: string;
  icon: ElementType;
  className?: string;
};

const AccountCardNotOpened = ({
  type,
  accountTitle,
  icon,
  className,
}: Props) => {
  const { t } = useTranslation();
  const { openCreateAccountModal } = useAccountCreateModal(type);

  const Icon = icon;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => openCreateAccountModal()}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openCreateAccountModal();
        }
      }}
      className={cn(
        'group flex flex-col gap-[0.4rem] cursor-pointer',
        'w-full min-w-[18rem] max-w-[22rem]',
        'pb-[0.6rem] rounded-[1.6rem] bg-secondary-bg',
        className
      )}>
      <div className="account-card-dashed-border flex flex-col gap-[1.2rem] p-[1.6rem] pl-[2rem] rounded-[1.6rem] bg-background">
        <div className="flex h-[3.2rem] items-center justify-between gap-[1.2rem]">
          <span className="size-[3.2rem] rounded-full bg-secondary-bg border border-stroke-bg flex items-center justify-center shrink-0">
            <Icon className="size-[2rem] text-primary" />
          </span>

          <span className="flex h-[3.2rem] items-center gap-[0.4rem] pl-[0.8rem] pr-[1.2rem] rounded-[1rem] border border-stroke bg-background headline5 text-primary group-hover:bg-secondary-bg transition-colors">
            <PlusIcon className="size-[1.6rem]" />
            {t('personalAccount.overview.notOpenedAccount.openButton')}
          </span>
        </div>

        <div className="flex flex-col gap-[0.2rem]">
          <div className="flex h-[2.2rem] items-center">
            <span className="medium5 text-primary truncate">
              {accountTitle}
            </span>
          </div>

          <span className="h-[2rem] medium5 text-secondary">
            {formatBalance(0, NUMERIC_CONFIG.precision.default)}
          </span>
        </div>
      </div>

      <div className="flex h-[3.2rem] items-center justify-between px-[0.8rem] text-stroke">
        <span className="flex h-[3.2rem] items-center gap-[0.4rem] pl-[0.8rem] pr-[1.2rem] rounded-[1rem] headline5">
          <TransferIcon className="size-[1.6rem]" />
          {t('personalAccount.overview.notOpenedAccount.transferButton')}
        </span>

        <span className="size-[3.2rem] flex items-center justify-center">
          <EllipsisVerticalIcon className="size-[1.6rem]" />
        </span>
      </div>
    </div>
  );
};

export { AccountCardNotOpened };
